import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  BackendService,
  BaseApiService,
  BaseStatusResponse,
  ETimeNumericFormat,
  getLocalTimezone,
  RequestFacadeModel,
  RequestModel,
  RequestType,
} from '@cf/core';
import { DateTime } from 'luxon';
import { EMPTY, from, mergeMap, Observable, Subject, take, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';

import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { ModalService } from '@cf/shared/dialog';
import { ofType } from '@ngneat/effects';
import { Actions } from '@ngneat/effects-ng';
import { TranslateService } from '@tolgee/ngx';
import {
  connectCalendar,
  connectCalendarFinished,
  GoogleCalendarDto,
  RegisterFormDto,
} from '../auth';
import { CalendarStoreService } from '../calendars/calendar-store.service';
import { loadInvitations, loadOrganizations } from '../organizations/effects/actions';
import { OrganizationsService } from '../organizations/services/organizations.service';
import { EditAccountDto, SetPasswordDto } from './dtos';
import { FirstDayOfWeekEnum, TimeFormatEnum, TimezoneEnum } from './enums';
import { LanguageEnum } from './helpers';
import { IAccount } from './interfaces';
import { AccountQuery, AccountStore } from './store';

const PROFILE_STORAGE_KEY = 'cfPreviousProfile';

@Injectable({
  providedIn: 'root',
})
export class AccountService extends BaseApiService {
  configUpdated$: Subject<any> = new Subject<any>();

  contactsImportState$: Subject<'loading' | 'completed'> = new Subject();

  public isAuthorized$: Observable<boolean> = this.accountQuery.account$.pipe(
    map((account) => !!account.id),
  );

  public account$: Observable<IAccount> = this.accountQuery.account$;

  $account = toSignal(this.accountQuery.select(), { initialValue: this.accountQuery.getValue() });

  private initialCalendarCheck = false;

  get account(): IAccount {
    return this.accountQuery.getValue();
  }

  get timeFormat(): 'HH:mm' | 'hh:mm A' {
    if (!this.account?.time_format) {
      return 'HH:mm';
    }
    return this.account.time_format === TimeFormatEnum['12h'] ? 'hh:mm A' : 'HH:mm';
  }

  get timeNumericFormat(): ETimeNumericFormat {
    if (!this.account?.time_format) {
      return ETimeNumericFormat.TIME_12;
    }
    return this.account.time_format === TimeFormatEnum['12h']
      ? ETimeNumericFormat.TIME_12
      : ETimeNumericFormat.TIME_24;
  }

  get weekFormat(): 'week' | 'isoWeek' {
    if (!this.account?.first_day_of_week) {
      return 'week';
    }
    switch (this.account.first_day_of_week) {
      case FirstDayOfWeekEnum.Monday:
        return 'isoWeek';
      case FirstDayOfWeekEnum.Sunday:
        return 'week';
      default:
        return 'week';
    }
  }

  get timezone(): TimezoneEnum {
    if (this.account?.timezone) {
      return this.account.timezone;
    } else {
      return getLocalTimezone() as TimezoneEnum;
    }
  }

  get localTimezone(): TimezoneEnum {
    return getLocalTimezone() as TimezoneEnum;
  }

  get isLocalTimezone(): boolean {
    return (
      DateTime.local({ zone: this.timezone }).offset ===
      DateTime.local({ zone: getLocalTimezone() }).offset
    );
  }

  #actions = inject(Actions);
  translateService = inject(TranslateService);

  constructor(
    public override backendService: BackendService,
    private readonly calendarStoreService: CalendarStoreService,
    private readonly accountQuery: AccountQuery,
    private readonly accountStore: AccountStore,
    private readonly route: ActivatedRoute,
    public organizationsService: OrganizationsService,
  ) {
    super(backendService, 'account');

    this.loadAccountFromStorage();
  }

  loadAccountFromStorage() {
    const savedAccount = localStorage.getItem(PROFILE_STORAGE_KEY);
    if (savedAccount) {
      this.accountStore.update(JSON.parse(savedAccount));
    }
  }

  updateCalendarAlert(showCalendarAlert: boolean) {
    this.accountStore.update({ ...this.account, showCalendarAlert });
  }

  public register(registerFormDto: RegisterFormDto): Observable<IAccount> {
    const request: RequestModel<RegisterFormDto> = new RequestModel<RegisterFormDto>({
      url: this.getFullUrl('signup'),
      requestBody: registerFormDto,
    });
    const requestFacade: RequestFacadeModel<RegisterFormDto> =
      new RequestFacadeModel<RegisterFormDto>({
        requestType: RequestType.post,
        request,
      });
    return this.send<IAccount, RegisterFormDto>(requestFacade);
  }

  private readonly modalService = inject(ModalService);

  connectCalendarFromGoogle(
    googleCalendarDto: GoogleCalendarDto,
    showConfirmation = true,
  ): Observable<BaseStatusResponse> {
    const request: RequestModel<GoogleCalendarDto> = new RequestModel<GoogleCalendarDto>({
      url: 'connections/google/',
      requestBody: googleCalendarDto,
    });
    const requestFacade: RequestFacadeModel<GoogleCalendarDto> =
      new RequestFacadeModel<GoogleCalendarDto>({
        requestType: RequestType.post,
        request,
      });

    if (!showConfirmation && requestFacade.request.requestBody) {
      requestFacade.request.requestBody.set_to_schedules = true;
      requestFacade.request.requestBody.set_meeting_tool_to_locations = true;
    }

    if (this.calendarStoreService.calendars.length === 0 && showConfirmation) {
      return from(
        this.modalService.openConfirmation({
          heading: this.translateService.instant('calendars.apply_google_calendar_title'),
          text: this.translateService.instant('calendars.apply_google_calendar_text'),
          buttonName: this.translateService.instant('calendars.save_and_apply'),
          cancelButtonName: this.translateService.instant('calendars.set_up_later'),
          checkBoxTitle: this.translateService.instant('calendars.apply_google_calendar_checkbox'),
        }).result,
      ).pipe(
        mergeMap((result) => {
          requestFacade.request.requestBody!.set_to_schedules = result.submit ?? result;
          requestFacade.request.requestBody!.set_meeting_tool_to_locations =
            result.checkBoxValue ?? result;
          return this.connectGoogleCalendar(requestFacade);
        }),
        catchError(() => this.connectGoogleCalendar(requestFacade)),
      );
    } else {
      return this.connectGoogleCalendar(requestFacade);
    }
  }

  private connectGoogleCalendar(requestFacade: RequestFacadeModel<any>) {
    return this.send<BaseStatusResponse, GoogleCalendarDto>(requestFacade).pipe(
      finalize(() => {
        this.calendarStoreService.getCalendars();
      }),
    );
  }

  connectContactsFromGoogle(googleCalendarDto: GoogleCalendarDto): Observable<BaseStatusResponse> {
    this.contactsImportState$.next('loading');
    const request: RequestModel<GoogleCalendarDto> = new RequestModel<GoogleCalendarDto>({
      url: 'connections/google-contacts/',
      requestBody: googleCalendarDto,
    });
    const requestFacade: RequestFacadeModel<GoogleCalendarDto> =
      new RequestFacadeModel<GoogleCalendarDto>({
        requestType: RequestType.post,
        request,
      });
    return this.send<BaseStatusResponse, GoogleCalendarDto>(requestFacade).pipe(
      finalize(() => {
        this.contactsImportState$.next('completed');
      }),
    );
  }

  public getAccountProfile$(): Observable<IAccount> {
    const request: RequestModel = new RequestModel<null>({
      url: this.getFullUrl(),
      requestBody: null,
    });
    const requestFacade: RequestFacadeModel = new RequestFacadeModel<null>({
      requestType: RequestType.get,
      request,
    });
    return this.send<IAccount, null>(requestFacade).pipe(
      tap((account: IAccount) => {
        localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(account));
        this.checkUserSignUpWithGoogleFromWPSite(account);
        this.#actions.dispatch(loadOrganizations());
        this.#actions.dispatch(loadInvitations());

        if (!account.name.trim()) {
          account.name = account.email;
        }
        this.accountStore.update({
          ...account,
          showCalendarAlert:
            !account.has_any_connected_calendar || account.has_any_unusable_calendar,
        });
      }),
      catchError((error: HttpErrorResponse) => {
        this.accountStore.reset();
        this.clearProfileStorage(PROFILE_STORAGE_KEY);

        return throwError(() => error);
      }),
    );
  }

  checkUserSignUpWithGoogleFromWPSite(account: IAccount) {
    if (
      account.show_connect_calendar &&
      this.route.snapshot.queryParams['is_google_login'] &&
      !this.initialCalendarCheck
    ) {
      this.initialCalendarCheck = true;
      this.#actions.dispatch(connectCalendar({ newUser: true }));
      this.#actions.pipe(ofType(connectCalendarFinished), take(1)).subscribe();
    }
  }

  public getAccountProfile(): void {
    this.getAccountProfile$()
      .pipe(
        take(1),
        catchError(() => EMPTY),
      )
      .subscribe();
  }

  public updatePassword(setPasswordDto: SetPasswordDto): Observable<unknown> {
    const request: RequestModel<SetPasswordDto> = new RequestModel<SetPasswordDto>({
      url: this.getFullUrl('password'),
      requestBody: setPasswordDto,
      successMessage: {
        message: this.translateService.instant('account.password.success_message'),
        title: this.translateService.instant('account.password.title'),
      },
    });
    const requestFacade: RequestFacadeModel<SetPasswordDto> =
      new RequestFacadeModel<SetPasswordDto>({
        requestType: RequestType.put,
        request,
      });
    return this.send<unknown, SetPasswordDto>(requestFacade);
  }

  public updateAvatar(dto: any): Observable<{ cropped_image: string }> {
    const request: RequestModel<any> = new RequestModel<any>({
      url: this.getFullUrl('picture'),
      requestBody: dto,
      successMessage: {
        message: this.translateService.instant('account.avatar.update_success'),
        title: this.translateService.instant('account.avatar.title'),
      },
    });
    const requestFacade: RequestFacadeModel<any> = new RequestFacadeModel<any>({
      requestType: RequestType.put,
      request,
    });
    return this.send<any, any>(requestFacade);
  }

  public removeAvatar(): Observable<any> {
    const request: RequestModel<any> = new RequestModel<any>({
      url: this.getFullUrl('picture'),
      successMessage: {
        message: this.translateService.instant('account.avatar.remove_success'),
        title: this.translateService.instant('account.avatar.title'),
      },
    });
    const requestFacade: RequestFacadeModel<any> = new RequestFacadeModel<any>({
      requestType: RequestType.delete,
      request,
    });
    return this.send<any, any>(requestFacade);
  }

  public updateProfile(accountDto: EditAccountDto, successMsg?: string): Observable<unknown> {
    const request: RequestModel<EditAccountDto> = new RequestModel<EditAccountDto>({
      url: this.getFullUrl(),
      requestBody: accountDto,
      skipNotify: true,
      successMessage: successMsg
        ? {
            message: successMsg,
          }
        : undefined,
    });
    const requestFacade: RequestFacadeModel<EditAccountDto> =
      new RequestFacadeModel<EditAccountDto>({
        requestType: RequestType.patch,
        request,
      });
    return this.send<unknown, EditAccountDto>(requestFacade).pipe(
      finalize(() => this.getAccountProfile()),
    );
  }

  public updatePartialAccount(profileDto: Partial<EditAccountDto>): Observable<unknown> {
    const request: RequestModel<Partial<EditAccountDto>> = new RequestModel<
      Partial<EditAccountDto>
    >({
      url: this.getFullUrl(),
      requestBody: profileDto,
      // successMessage: {
      //   message: 'Updated',
      //   title: 'Profile',
      // },
    });
    const requestFacade: RequestFacadeModel<Partial<EditAccountDto>> = new RequestFacadeModel<
      Partial<EditAccountDto>
    >({
      requestType: RequestType.patch,
      request,
    });
    return this.send<unknown, Partial<EditAccountDto>>(requestFacade).pipe(
      finalize(() => this.getAccountProfile()),
    );
  }

  updateEmail(dto: { email?: string; password?: string; token?: string }) {
    const request: RequestModel<any> = new RequestModel<any>({
      url: this.getFullUrl('email'),
      requestBody: dto,
    });
    const requestFacade: RequestFacadeModel<any> = new RequestFacadeModel<any>({
      requestType: dto.token ? RequestType.put : RequestType.post,
      request,
    });
    return this.send<any, any>(requestFacade);
  }

  removeNewEmail() {
    const request: RequestModel<any> = new RequestModel<any>({
      url: this.getFullUrl('email'),
    });
    const requestFacade: RequestFacadeModel<any> = new RequestFacadeModel<any>({
      requestType: RequestType.delete,
      request,
    });
    return this.send<any, any>(requestFacade);
  }

  public changeLang(language: LanguageEnum): Observable<unknown> {
    const request: RequestModel<any> = new RequestModel<any>({
      url: this.getFullUrl(),
      requestBody: { language },
    });
    const requestFacade: RequestFacadeModel<any> = new RequestFacadeModel<any>({
      requestType: RequestType.patch,
      request,
    });
    return this.send<unknown, any>(requestFacade);
  }

  public logout(): void {
    this.clearProfileStorage(PROFILE_STORAGE_KEY);
    this.accountStore.reset();
  }

  public clearProfileStorage(storageItem: string): void {
    localStorage.removeItem(storageItem);
  }
}
